import React, { useRef, useEffect, useState } from "react";
import {
  Button,
  Space,
  Progress,
  Alert,
  Typography,
  Card,
  message,
} from "antd";
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  StopOutlined,
  CameraOutlined,
  RedoOutlined,
  VideoCameraOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useVideoRecorder } from "../../hooks/useVideoRecorder";

const { Title, Text } = Typography;

const VideoRecorder = ({
  onRecordingComplete,
  maxDuration = 180,
  showPreview = true,
}) => {
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSavedPreview, setShowSavedPreview] = useState(false);

  const {
    stream,
    recordedVideo,
    isRecording,
    recordingTime,
    error,
    isCameraReady,
    initializeCamera,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    stopCamera,
    cleanup,
    resetRecording,
  } = useVideoRecorder();

  // Update video element when stream changes
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch((err) => {
        console.warn("Video play failed:", err);
      });
    }
  }, [stream]);

  // Keep video playing during recording
  useEffect(() => {
    if (videoRef.current && stream && isRecording) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch((err) => {
        console.warn("Video play during recording failed:", err);
      });
    }
  }, [isRecording, stream]);

  // Update preview when recording is complete
  useEffect(() => {
    if (previewRef.current && recordedVideo) {
      previewRef.current.src = recordedVideo.url;
      previewRef.current.load();
    }
  }, [recordedVideo]);

  // Auto-stop at max duration
  useEffect(() => {
    if (recordingTime >= maxDuration && isRecording) {
      handleStopRecording();
      message.info(
        `Recording automatically stopped at ${formatTime(maxDuration)}`,
      );
    }
  }, [recordingTime, maxDuration, isRecording]);

  // Call onRecordingComplete when video is ready
  useEffect(() => {
    if (recordedVideo && onRecordingComplete) {
      onRecordingComplete(recordedVideo);
    }
  }, [recordedVideo, onRecordingComplete]);

  // Reset showSavedPreview when recording starts again
  useEffect(() => {
    if (isRecording) {
      setShowSavedPreview(false);
    }
  }, [isRecording]);

  const handleStartCamera = async () => {
    try {
      await initializeCamera();
      setShowSavedPreview(false);
    } catch (err) {
      console.error("Camera error:", err);
    }
  };

  const handleStartRecording = async () => {
    try {
      await startRecording();
      setShowSavedPreview(false);
      message.success("Recording started!");
    } catch (err) {
      console.error("Recording error:", err);
      message.error("Failed to start recording");
    }
  };

  const handleStopRecording = async () => {
    setIsProcessing(true);
    try {
      const videoData = await stopRecording();

      if (videoData) {
        const processedVideoData = {
          ...videoData,
          duration: Math.round(videoData.duration),
        };

        message.success("Recording saved! Review your video below.");
        setShowSavedPreview(true);
        stopCamera();
        onRecordingComplete(processedVideoData);
      } else {
        throw new Error("No video data received");
      }
    } catch (err) {
      console.error("Error stopping recording:", err);
      message.error("Failed to save recording: " + err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRecordAgain = () => {
    resetRecording();
    setShowSavedPreview(false);
    message.info("Ready to record again");
  };

  const handleStopCamera = () => {
    stopCamera();
    setShowSavedPreview(false);
    message.info("Camera stopped");
  };

  const handlePauseRecording = () => {
    pauseRecording();
  };

  const handleResumeRecording = () => {
    resumeRecording();
    setShowSavedPreview(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const getTimeColor = () => {
    const percentage = (recordingTime / maxDuration) * 100;
    if (percentage > 90) return "#ff4d4f";
    if (percentage > 75) return "#faad14";
    return "#52c41a";
  };

  const getRemainingTime = () => {
    return maxDuration - recordingTime;
  };

  const videoStyle = {
    transform: "scaleX(-1)",
    WebkitTransform: "scaleX(-1)",
  };

  if (error) {
    return (
      <div>
        <div className="bg-[#2f3e52] rounded-xl h-64 md:h-96 flex flex-col items-center justify-center text-center px-6">
          <CameraOutlined
            style={{
              fontSize: 42,
              color: "#ef4444",
              marginBottom: 12,
            }}
          />

          <Text className="text-white text-lg font-semibold mb-2">
            Camera access denied
          </Text>

          <Text className="text-white/70 mb-6">
            Please allow camera and microphone access to continue.
          </Text>

          <Button
            type="primary"
            danger
            icon={<CameraOutlined />}
            onClick={handleStartCamera}
          >
            Retry Camera Access
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Camera Setup - Show when no stream */}
      {!stream && !recordedVideo && !showSavedPreview && (
        <div>
          <div className="bg-[#2f3e52] rounded-xl h-64 md:h-96 flex flex-col items-center justify-center text-center relative">
            <div
              className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mb-4"
              onClick={handleStartCamera}
            >
              <VideoCameraOutlined className="text-white text-2xl" />
            </div>

            <Text className="text-white text-lg font-semibold">
              Your camera preview will appear here
            </Text>
            <Text className="text-white/70 text-sm mt-1">
              Click "Start Recording" when you're ready to begin
            </Text>
          </div>

          {/* Controls */}
          <div className="flex justify-center items-center gap-6 mt-6">
            {/* Play Button */}
            {/* <Button
              shape="circle"
              size="large"
              icon={<PlayCircleOutlined />}
              disabled
            /> */}

            {/* Start Recording */}
            <Button
              size="large"
              type="primary"
              danger
              icon={<VideoCameraOutlined />}
              onClick={handleStartCamera}
            >
              Start Camera
            </Button>

            {/* Stop Button */}
            {/* <Button
              shape="circle"
              size="large"
              icon={<StopOutlined />}
              disabled
            /> */}
          </div>
        </div>
      )}

      {/* Camera Ready but Not Recording */}
      {stream &&
        isCameraReady &&
        !isRecording &&
        !recordedVideo &&
        recordingTime === 0 &&
        !showSavedPreview && (
          <Card className="shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <Title level={4} className="mb-0">
                📹 Camera Ready
              </Title>
              <Button
                onClick={handleStopCamera}
                size="small"
                icon={<StopOutlined />}
                danger
              >
                Stop Camera
              </Button>
            </div>

            <div className="bg-black rounded-lg overflow-hidden relative">
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-64 md:h-96 object-cover"
                style={videoStyle}
              />
              <div className="absolute top-4 left-4 flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <Text className="text-white font-semibold">LIVE PREVIEW</Text>
              </div>
            </div>

            <div className="flex flex-col items-center mt-6 gap-2">
              <Button
                shape="circle"
                size="large"
                type="primary"
                danger
                icon={<VideoCameraOutlined />}
                onClick={handleStartRecording}
              />
              <span className="text-sm font-medium">Start Recording</span>
            </div>

            <div className="text-center mt-4">
              <Text type="secondary">
                Maximum recording time: {formatTime(maxDuration)}
              </Text>
              <br />
              <Text type="secondary" className="text-sm">
                Recording will auto-stop at {formatTime(maxDuration)}
              </Text>
            </div>
          </Card>
        )}

      {/* Live Recording Interface */}
      {stream && isRecording && !showSavedPreview && (
        <Card className="shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <Title level={4} className="mb-0 text-red-600">
              🔴 Recording...
            </Title>
            <div className="text-right">
              <div
                className="text-lg font-semibold"
                style={{ color: getTimeColor() }}
              >
                {formatTime(recordingTime)}
              </div>
              <Text type="secondary" className="text-sm">
                {formatTime(getRemainingTime())} remaining
              </Text>
            </div>
          </div>

          <div className="bg-black rounded-lg overflow-hidden relative">
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-64 md:h-96 object-cover"
              style={videoStyle}
            />

            <div className="absolute top-4 left-4 flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <Text className="text-white font-semibold">
                REC {formatTime(recordingTime)}
              </Text>
            </div>

            <div className="absolute bottom-4 left-4">
              <Text className="text-white bg-red-500 px-2 py-1 rounded">
                Time Left: {formatTime(getRemainingTime())}
              </Text>
            </div>
          </div>

          <div className="flex justify-center mt-6">
            <Space size="large">
              {/* Pause */}
              <div className="flex flex-col items-center">
                <Button
                  shape="circle"
                  type="default"
                  onClick={handlePauseRecording}
                  icon={<PauseCircleOutlined />}
                />
                <Text className="text-sm text-gray-500 mt-1">Pause</Text>
              </div>

              {/* Stop */}
              <div className="flex flex-col items-center">
                <Button
                  shape="circle"
                  type="primary"
                  danger
                  onClick={handleStopRecording}
                  icon={<SaveOutlined />}
                  loading={isProcessing}
                />
                <Text className="text-sm text-gray-500 mt-1">
                  {isProcessing ? "Saving..." : "Save"}
                </Text>
              </div>
            </Space>
          </div>

          <Progress
            percent={Math.min((recordingTime / maxDuration) * 100, 100)}
            strokeColor={getTimeColor()}
            className="mt-4"
            format={(percent) =>
              `${formatTime(recordingTime)} / ${formatTime(maxDuration)}`
            }
          />
        </Card>
      )}

      {/* Paused Recording - Shows LIVE camera feed */}
      {stream &&
        !isRecording &&
        !recordedVideo &&
        recordingTime > 0 &&
        !showSavedPreview && (
          <Card className="shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <Title level={4} className="mb-0">
                ⏸️ Recording Paused
              </Title>
              <div className="text-lg font-semibold text-orange-500">
                {formatTime(recordingTime)}
              </div>
            </div>

            <div className="bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-64 md:h-96 object-cover"
                style={videoStyle}
              />
              <div className="absolute top-4 left-4 flex items-center space-x-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
                <Text className="text-white font-semibold">PAUSED</Text>
              </div>
            </div>
            <div className="flex justify-center space-x-4 mt-6">
              {/* Resume */}
              <Button
                shape="circle"
                type="primary"
                size="large"
                title="Resume"
                onClick={handleResumeRecording}
                icon={<PlayCircleOutlined />}
              />

              {/* Save */}
              <Button
                shape="circle"
                size="large"
                title="Save Recording"
                onClick={handleStopRecording}
                icon={<SaveOutlined />}
                loading={isProcessing}
              />
            </div>
          </Card>
        )}

      {/* Saved Recording Preview - Shows the recorded video */}
      {recordedVideo && showPreview && showSavedPreview && (
        <Card className="shadow-sm">
          <Title level={4} className="mb-1 text-center md:text-left">
            🎉 Recording Complete!
          </Title>

          <Text
            type="secondary"
            className="block mb-4 text-center md:text-left"
          >
            Your video has been saved successfully
          </Text>

          {/* Video Preview */}
          <div className="bg-black rounded-lg overflow-hidden mb-4">
            <video
              ref={previewRef}
              autoPlay
              playsInline
              loop
              controls={false}
              className="
          w-full
          h-56
          sm:h-64
          md:h-80
          lg:h-96
          object-cover
        "
              style={{ transform: "scaleX(-1)" }}
            />
          </div>

          {/* Video Info */}
          <div className="flex justify-center md:justify-between items-center mb-4">
            <Space direction="vertical" className="text-center md:text-left">
              <Text type="secondary">
                Size: {(recordedVideo.fileSize / (1024 * 1024)).toFixed(2)} MB
              </Text>
            </Space>
          </div>

          {/* Action Buttons */}

          <div className="flex flex-col items-center">
            <Button
              shape="circle"
              size="large"
              danger
              title="Record Again"
              onClick={handleRecordAgain}
              icon={<RedoOutlined />}
            />
            <Text className="text-sm text-gray-500 mt-1">Record Again</Text>
          </div>

          {/* New Recording */}
          {/* <div className="flex flex-col items-center">
            <Button
              shape="circle"
              type="primary"
              size="large"
              title="New Recording"
              onClick={() => {
                resetRecording();
                handleStartCamera();
              }}
              icon={<CameraOutlined />}
            />
            <Text className="text-sm text-gray-500 mt-1">New Recording</Text>
          </div> */}
        </Card>
      )}
    </div>
  );
};

export default VideoRecorder;
